import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@codes/ui-kit/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@codes/ui-kit/components/ui/avatar';
import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Card, CardContent } from '@codes/ui-kit/components/ui/card';
import { Separator } from '@codes/ui-kit/components/ui/separator';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@codes/ui-kit/components/ui/sheet';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, Clock, Loader2, Mail, Phone, User as UserIcon, XCircle } from 'lucide-react';

import { type KeyView, type ValidatedUserProfile, useValidateApiKey } from '@codes/openapi-keys';

import { MaskedKeyDisplay } from './MaskedKeyDisplay';

interface OpenApiKeyValidationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    keyToValidate: KeyView | null;
}

export const OpenApiKeyValidationDialog = ({ open, onOpenChange, keyToValidate }: OpenApiKeyValidationDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const validateMutation = useValidateApiKey({
        onError: error => {
            toast({
                title: t('openApiKeys.validation.error'),
                description: error.message || t('openApiKeys.validation.invalidKey'),
                variant: 'destructive',
            });
        },
    });

    // Auto-validate when dialog opens with a key
    useEffect(() => {
        if (open && keyToValidate?.apiKey) {
            validateMutation.mutate({ apiKey: keyToValidate.apiKey });
        }
    }, [open, keyToValidate?.apiKey]);

    const handleClose = () => {
        validateMutation.reset();
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[600px]">
                <SheetHeader aria-describedby={undefined}>
                    <SheetTitle>
                        {keyToValidate?.name || keyToValidate?.id || t('openApiKeys.card.unnamedKey')}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex flex-col gap-y-6 py-6">
                    {/* Key Info */}
                    {keyToValidate && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                {t('openApiKeys.validation.inputLabel')}
                            </label>
                            <MaskedKeyDisplay value={keyToValidate.apiKey || ''} label="" />
                        </div>
                    )}

                    {/* Validation Result */}
                    {validateMutation.isPending && (
                        <div className="flex items-center gap-2 p-4 rounded-lg bg-muted/50">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                                {t('openApiKeys.validation.validating')}
                            </span>
                        </div>
                    )}

                    {validateMutation.isError && (
                        <Alert variant="destructive">
                            <XCircle className="h-4 w-4" />
                            <AlertTitle>{t('openApiKeys.validation.invalid')}</AlertTitle>
                            <AlertDescription>{t('openApiKeys.validation.invalidKey')}</AlertDescription>
                        </Alert>
                    )}

                    {validateMutation.isSuccess && validateMutation.data && (
                        <ValidationResult profile={validateMutation.data} />
                    )}
                </div>

                <SheetFooter className="pt-4">
                    <Button onClick={handleClose}>{t('common.close')}</Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

interface ValidationResultProps {
    profile: ValidatedUserProfile;
}

const ValidationResult = ({ profile }: ValidationResultProps) => {
    const { t } = useTranslation();

    const user = profile.$user;
    const site = profile.$site;
    const roles = profile.roles || [];
    const tokenExp = profile.$ctx?.identity?.exp;
    const tokenIat = profile.$ctx?.identity?.iat;

    const getInitials = (name?: string) => {
        if (!name) {
            return 'U';
        }
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <div className="space-y-3">
            {/* Success Banner */}
            <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertTitle>{t('openApiKeys.validation.valid')}</AlertTitle>
                <AlertDescription>API key is valid and authenticated successfully</AlertDescription>
            </Alert>

            {/* User Profile Card */}
            {user && (
                <Card>
                    <CardContent className="p-2 pt-4 pb-4 space-y-3">
                        {/* User Header */}
                        <div className="flex items-start gap-3">
                            <Avatar className="h-14 w-14">
                                {user.photo && <AvatarImage src={user.photo} alt={user.name} />}
                                <AvatarFallback className="text-base">{getInitials(user.name)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h4 className="text-base font-semibold truncate">{user.name}</h4>
                                    {user.stereo && (
                                        <Badge variant="secondary" className="text-xs h-5">
                                            {user.stereo}
                                        </Badge>
                                    )}
                                </div>
                                {user.nick && <p className="text-sm text-muted-foreground">@{user.nick}</p>}
                            </div>
                        </div>

                        <Separator className="my-2" />

                        {/* User Details */}
                        <div className="space-y-2">
                            {user.email && (
                                <div className="flex items-center gap-2.5 text-sm">
                                    <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="text-muted-foreground min-w-[55px]">Email</span>
                                    <span className="font-medium truncate">{user.email}</span>
                                </div>
                            )}
                            {user.phone && (
                                <div className="flex items-center gap-2.5 text-sm">
                                    <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="text-muted-foreground min-w-[55px]">Phone</span>
                                    <span className="font-medium">{user.phone}</span>
                                </div>
                            )}
                            {user.id && (
                                <div className="flex items-center gap-2.5 text-sm">
                                    <UserIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="text-muted-foreground min-w-[55px]">User ID</span>
                                    <span className="font-mono text-xs">{user.id}</span>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Token Information */}
            {(tokenIat || tokenExp) && (
                <Card>
                    <CardContent className="p-2 pt-4 pb-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            <h4 className="font-semibold text-sm">Token Information</h4>
                        </div>

                        <Separator className="my-2" />

                        <div className="space-y-2">
                            {tokenIat && (
                                <div className="flex items-center gap-2.5 text-sm">
                                    <span className="text-muted-foreground min-w-[55px]">Issued</span>
                                    <span className="font-medium">
                                        {formatDistanceToNow(new Date(tokenIat * 1000), { addSuffix: true })}
                                    </span>
                                </div>
                            )}
                            {tokenExp && (
                                <div className="flex items-center gap-2.5 text-sm">
                                    <span className="text-muted-foreground min-w-[55px]">Expires</span>
                                    <span className="font-medium">
                                        {formatDistanceToNow(new Date(tokenExp * 1000), { addSuffix: true })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
